import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  BarChart3, 
  Server, 
  GitCompare, 
  Settings, 
  Search, 
  Globe,
  LogOut,
  User,
  Network,
  PlayCircle,
  Workflow,
  Shield,
  FolderOpen,
  Users,
  Folder,
  BookOpen,
  HeartPulse,
  Rss
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import ProjectSelector from './ProjectSelector';

const Sidebar = ({ onLogout, username }) => {
  const location = useLocation();

  const isAdmin = localStorage.getItem('userRole') === 'admin';
  
  const menuItems = [
    { path: '/', icon: BarChart3, label: 'Дашборд' },
    { path: '/hosts', icon: Server, label: 'Хосты' },
    { path: '/wiki', icon: BookOpen, label: 'Вики' },
    { path: '/autodiscovery', icon: Globe, label: 'АвтоДискавери' },
    { path: '/graph', icon: Network, label: 'Граф' },
    { path: '/tasks', icon: PlayCircle, label: 'Задачи' },
    { path: '/scan', icon: Workflow, label: 'Сканирование' },
    { path: '/vulnerabilities', icon: Shield, label: 'Уязвимости' },
    { path: '/healthcheck', icon: HeartPulse, label: 'Healthcheck' },
    { path: '/diff', icon: GitCompare, label: 'Дифф' },
    { path: '/config', icon: Settings, label: 'Конфиг' },
    { path: '/data-feeds', icon: Rss, label: 'Data Feeds' },
    { path: '/files', icon: Folder, label: 'Файлы' },
    { path: '/projects', icon: FolderOpen, label: 'Проекты' },
    ...(isAdmin ? [{ path: '/admin', icon: Users, label: 'Админка' }] : []),
  ];

  return (
    <div style={{
      width: '250px',
      height: '100vh',
      backgroundColor: 'var(--bg-secondary)',
      borderRight: '1px solid var(--border-color)',
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 1000,
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, var(--color-primary), #6366f1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Shield size={18} color="white" />
        </div>
        <div>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '700',
            color: 'var(--text-primary)',
            letterSpacing: '-0.01em',
          }}>
            SecWeb
          </h2>
          <p style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
          }}>
            Платформа безопасности
          </p>
        </div>
      </div>

      {/* User Info */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid var(--border-color)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px',
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            backgroundColor: 'var(--color-primary)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <User size={16} color="white" />
          </div>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
            }}>
              {username}
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-secondary)',
            }}>
              {localStorage.getItem('userRole') === 'admin' ? 'Администратор' : 'Пользователь'}
            </div>
          </div>
        </div>
        <ProjectSelector />
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, padding: '16px 0' }}>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 24px',
                color: isActive ? 'var(--nav-active-color)' : 'var(--text-secondary)',
                backgroundColor: isActive ? 'var(--nav-active-bg)' : 'transparent',
                borderRight: isActive ? '3px solid var(--nav-active-color)' : '3px solid transparent',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: isActive ? '500' : '400',
                transition: 'all 0.2s',
              }}
            >
              <Icon size={18} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Theme Toggle and Logout */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid var(--border-color)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{
            fontSize: '14px',
            color: 'var(--text-secondary)',
          }}>
            Тема
          </span>
          <ThemeToggle />
        </div>
        
        <button
          onClick={onLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            width: '100%',
            backgroundColor: 'transparent',
            border: 'none',
            color: 'var(--text-secondary)',
            fontSize: '14px',
            cursor: 'pointer',
            borderRadius: '6px',
            transition: 'all 0.2s',
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = 'var(--bg-tertiary)';
            e.target.style.color = 'var(--color-danger)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = 'var(--text-secondary)';
          }}
        >
          <LogOut size={18} />
          Выход
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
